import type { FC } from 'react';
import type { LoginRequest } from '@/queries/auth/auth.schema';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useFingerprintRequestIdNoCache } from '@/components/entities/analytics/components/FingerprintProvider/hooks/fingerPrintRequestId';
import { UserDeviceActivityType } from '@/components/entities/analytics/components/FingerprintProvider/types/fingerPrintType';
import { indentifyUser } from '@/components/entities/analytics/components/PostHog/utils/indentifyUser';
import { gtmUserProfileStore, pushCustomGtmEventToDataLayer } from '@/components/entities/analytics/gtm';
import { ExternalAuthButtons } from '@/components/entities/auth/ExternalAuthButtons';
import { Icon } from '@/components/entities/Sprite';
import { Button } from '@/components/ui/Button/button';
import { Input } from '@/components/ui/Input/input';
import Toast from '@/components/ui/Toast/toast';
import { useRouter } from '@/i18n/routing';
import { preloadCongratsVideo } from '@/modals/modules/auth/+CongratsModal/components/congratsVideo';
import { getPendingRegistration, getPendingRegistrationEmail, setHadSuccessLogin, setPendingRegistration } from '@/modals/modules/auth/RegisterModal/components/registrationSession.utils';
import openModal from '@/modals/utils/openModal';
import { authApi } from '@/queries/auth/auth.api';
import { APIError } from '@/queries/httpClient';
import { QueriesKeys } from '@/queries/queriesKeys';
import { tokenManager } from '@/queries/token-manager';
import { useAppDispatch } from '@/redux/hooks';
import { checkToken } from '@/redux/slices/userSlice';
import { cn } from '@/utils/cn';
import { useResendVerification } from '../../hooks/useResendVerification';
import { StepOfLogin } from '../type';

interface LoginPopupFormProps {
  closeModal: () => void;
  setStepOfLogin: (step: StepOfLogin) => void;
}

export const LoginPopupForm: FC<LoginPopupFormProps> = ({ closeModal, setStepOfLogin }) => {
  const t = useTranslations('sign-in');
  const dispatch = useAppDispatch();
  const { mutation, timer } = useResendVerification();
  const queryClient = useQueryClient();
  const router = useRouter();

  const revalidateQueries = async () => {
    await queryClient.refetchQueries({ queryKey: [QueriesKeys.user] });
    await queryClient.refetchQueries({ queryKey: [QueriesKeys.rewardsGrouped] });
    await queryClient.refetchQueries({ queryKey: [QueriesKeys.gamesWithWinsInfinite] });
    // INFO: every login need update cache for gamification
    await queryClient.refetchQueries({ queryKey: [QueriesKeys.mePlayerProfile] });
    await queryClient.refetchQueries({ queryKey: [QueriesKeys.mePlayerGamificationRewards] });
    await queryClient.refetchQueries({ queryKey: [QueriesKeys.mePlayerGamificationGroupedRewards] });
    await queryClient.refetchQueries({ queryKey: [QueriesKeys.verifications] });

    indentifyUser(queryClient);
  };

  const params = useSearchParams();
  const userId = params.get('userId');

  const reusedEmail = userId ? getPendingRegistration(userId) : null;

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    setError,
    watch,
    clearErrors,
  } = useForm<LoginRequest>({
    defaultValues: {
      username: reusedEmail ?? getPendingRegistrationEmail() ?? '',
      password: '',
    },
  });

  const username = watch('username');
  const password = watch('password');

  const isFormValid = username.trim() !== '' && password.trim() !== '';

  useEffect(() => {
    if (reusedEmail) {
      Toast({
        type: 'success',
        title: t('email-confirmed'),
        description: t('email-confirmed-description'),
      });
    }
  }, [reusedEmail]);

  useEffect(() => {
    clearErrors(['username', 'password']);
  }, [username, password, clearErrors]);

  const { getFingerprintRequestIdNoCache } = useFingerprintRequestIdNoCache({ activityType: UserDeviceActivityType.Login });
  const hasPendingRegistration = getPendingRegistrationEmail() !== ''
    && username.trim().toLowerCase() === getPendingRegistrationEmail()?.toLowerCase();

  const onSubmit = async (data: LoginRequest) => {
    try {
      if (hasPendingRegistration) {
        preloadCongratsVideo();
      }

      const requestId = await getFingerprintRequestIdNoCache();
      const headers: Record<string, string> = {
        'x-fingerprint-request-id': requestId,
      };

      await authApi.login(data, headers).then(async () => {
        await dispatch(checkToken()).unwrap();
        // Private routes prefetched as a guest are cached as redirects to the login modal;
        // drop the client router cache so navigation uses the new session.
        router.refresh();

        setHadSuccessLogin();

        const gtmUserData = {
          id: tokenManager.getUserId() ?? undefined,
          email: data.username,
        };

        gtmUserProfileStore.cache(gtmUserData);
        pushCustomGtmEventToDataLayer('user_logged_in');

        Toast({
          type: 'success',
          title: t('login-successful'),
          description: t('login-successful-description'),
        });
      });

      closeModal();
      await revalidateQueries();
    } catch (error) {
      const isApiError = error instanceof APIError;

      if (isApiError && error.code === 'ACCOUNT_SUSPENDED') {
        const errorMessage = t('user-suspended');
        setError('password', { message: errorMessage });
        setError('username', { message: errorMessage });
        return;
      }

      if (isApiError && error.code === 'POLICY_NOT_ACCEPTED') {
        preloadCongratsVideo();
        openModal('congratulations');
        return;
      }

      if (isApiError && error.code === 'EMAIL_NOT_VERIFIED') {
        const pendingRegistrationEmail = getPendingRegistrationEmail();
        if (!pendingRegistrationEmail) {
          setPendingRegistration(data.username);
        }
        const errorMessage = t('email-not-verified');
        setError('username', { message: errorMessage });
        return;
      }

      const message = t('incorrect-email-or-password');

      setError('password', { message });
      setError('username', { message });
    }
  };

  const disableResendButton = timer.isDisabled || mutation.isPending || username.trim() === '';
  const buttonVariantText = timer.count > 0 ? `${t('resend-code-in')} 0:${String(timer.count).padStart(2, '0')}` : `${t('resend-verification-email')}`;

  return (
    <div className="flex flex-col h-full">
      <form onSubmit={handleSubmit(onSubmit)} className="w-full h-full flex flex-col">
        <>
          <div className="flex items-center gap-2.5 lg:hidden">
            <div className="border border-c300/15 flex-1 " />
            <div className="relative">
              <Icon name="age-registration" className="size-16 text-primary-1" />
              <span className="text-white absolute
              top-1/2 left-1/2 -translate-x-1/2
              -translate-y-1/2 font-hepta font-extrabold text-[1.25rem] leading-[100%] tracking-[-1px]

              "
              >
                18+
              </span>
            </div>
            <div className="border border-c300/15 flex-1" />
          </div>
          <ExternalAuthButtons
            className="flex flex-col gap-3 mt-4"
            getFingerprintRequestIdNoCache={getFingerprintRequestIdNoCache}
          />

          <div className="flex items-center gap-2.5 py-4">
            <div className="border border-c300/15 flex-1 " />
            <span className="paragraph-s-regular text-c200">or</span>
            <div className="border border-c300/15 flex-1" />
          </div>

          <Input
            {...register('username', { required: t('email-required') })}
            type="email"
            label={t('email')}
            labelClassName="text-white paragraph-s-regular"
            placeholder={t('email-placeholder')}
            error={{
              isError: !!errors.username,
            }}
          />

          <Input
            {...register('password', { required: t('password-required') })}
            type="password"
            label={t('password')}
            labelClassName="text-white paragraph-s-regular mt-4"
            placeholder={t('password-placeholder')}
            error={{
              isError: !!errors.password,
            }}

          />

          {
            hasPendingRegistration
              ? (
                  <>
                    <div className="flex items-center mt-2 gap-1.5">
                      <button
                        type="button"
                        className={cn('text-c300 text-sm leading-5 font-regular flex items-center', timer.count > 0 ? 'text-c300 cursor-default' : 'text-white cursor-pointer')}
                        disabled={disableResendButton}
                        onClick={() => mutation.mutate(username)}
                      >
                        {buttonVariantText}

                      </button>
                      {mutation.isPending && <Icon name="loading" className="size-4 text-light animate-spin shrink-0" />}
                    </div>
                    <div className="p-2 bg-c300/10 rounded-xl flex gap-2 mt-3.5">
                      <Icon name="info-payment" className="size-4 shrink-0 text-c300" />
                      <div className="flex items-center gap-1.5">
                        <p className="text-c300 font-regular text-[0.75rem] leading-4">
                          {t('you-have-already-registered-but-you-havent-confirmed-your-email-yet')}
                        </p>
                      </div>
                    </div>
                  </>
                )
              : (
                  <div className="max-w-37.5 mt-3.5 lg:mt-4">
                    <Button
                      variant="tertiary"
                      type="button"
                      className="!paragraph-s-regular text-primary-3 w-full text-center cursor-pointer flex justify-start hover:text-white"
                      onClick={() => setStepOfLogin(StepOfLogin.RESET_PASSWORD)}
                    >
                      {t('forgot-password')}
                    </Button>
                  </div>
                )
          }

          {(errors.password || errors.username)
            && (
              <div className="flex items-center p-2 gap-2 bg-main-error/10 rounded-[0.75rem] mt-4">
                <Icon name="error" className="size-4 text-main-error" />
                <p className="text-main-error  paragraph-s-medium">{errors.password?.message || errors.username?.message}</p>
              </div>
            ) }
        </>

        <div className="mt-auto pt-4">
          <Button
            type="submit"
            className="w-full py-3"
            variant="primary"
            disabled={!isFormValid || isSubmitting}
            isLoading={isSubmitting}
          >
            {t('title')}
          </Button>

          <div className="flex items-center gap-1 text-sm justify-center w-full mt-6 lg:mt-4">
            <p className="text-c200 paragraph-s-medium">{t('join-sweepking-today')}</p>
            <button
              type="button"
              className="text-primary-1 paragraph-s-medium hover:text-white transition-colors duration-300 cursor-pointer"
              onClick={() => openModal('register')}
            >
              {t('sign-up')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};
